import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, Form, InputGroup, Alert } from 'react-bootstrap';
import { FaBox, FaTrash, FaPlus, FaMinus, FaEdit, FaPercent, FaDollarSign } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import '../../styles/order-page.css';

const ItensPedidoComponent = ({ items = [], onUpdateItems, readOnly = false }) => {
  const [cart, setCart] = useState(items);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editValues, setEditValues] = useState({ price: 0, discount: 0, discountType: 'value' });

  // Verificar se o usuário é admin ao carregar o componente
  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Verificar se o usuário é admin na tabela de administradores
          const { data: adminData, error } = await supabase
            .from('caex_administradores')
            .select('*')
            .eq('user_id', user.id)
            .eq('ativo', true)
            .single();
          
          if (!error && adminData) {
            setIsAdmin(true);
          }
        }
      } catch (error) {
        console.error('Erro ao verificar permissões de admin:', error);
      }
    };

    checkAdminRole();
  }, []);

  // Atualizar o carrinho quando os itens externos mudarem
  useEffect(() => {
    setCart(items);
  }, [items]);

  // Atualizar os itens do pedido quando o carrinho mudar
  useEffect(() => {
    if (onUpdateItems && typeof onUpdateItems === 'function') {
      onUpdateItems(cart);
    }
  }, [cart, onUpdateItems]);

  // Format currency values
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Calcular preço final do item considerando desconto
  const getItemFinalPrice = (item) => {
    const basePrice = item.customPrice || item.price;
    const discount = item.discount || 0;
    
    if (discount === 0) return basePrice;
    
    if (item.discountType === 'percentage') {
      return basePrice * (1 - discount / 100);
    } else {
      return Math.max(0, basePrice - discount);
    }
  };

  // Update cart item quantity
  const updateCartItemQuantity = (itemId, quantity) => {
    if (readOnly) return;
    
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCart(cart.map(item => 
      item.id === itemId 
        ? { ...item, quantity } 
        : item
    ));
  };

  // Remove item from cart
  const removeFromCart = (itemId) => {
    if (readOnly) return;
    setCart(cart.filter(item => item.id !== itemId));
  };

  // Clear cart
  const clearCart = () => {
    if (readOnly) return;
    setCart([]);
  };

  // Calculate cart total
  const calculateCartTotal = () => {
    return cart.reduce((total, item) => {
      const finalPrice = getItemFinalPrice(item);
      return total + (finalPrice * item.quantity);
    }, 0);
  };

  // Calcular total de desconto aplicado
  const calculateTotalDiscount = () => {
    return cart.reduce((totalDiscount, item) => {
      const basePrice = item.customPrice || item.price;
      const finalPrice = getItemFinalPrice(item);
      const itemDiscount = (basePrice - finalPrice) * item.quantity;
      return totalDiscount + itemDiscount;
    }, 0);
  };

  // Calculate total items
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Abrir modal de edição de item (apenas para admins)
  const openEditModal = (item) => {
    if (!isAdmin || readOnly) return;
    
    setEditingItem(item);
    setEditValues({
      price: item.customPrice || item.price,
      discount: item.discount || 0,
      discountType: item.discountType || 'value'
    });
    setShowEditModal(true);
  };

  // Salvar edições do item
  const saveItemEdit = () => {
    if (!editingItem) return;

    const updatedCart = cart.map(item => {
      if (item.id === editingItem.id) {
        return {
          ...item,
          customPrice: parseFloat(editValues.price),
          discount: parseFloat(editValues.discount),
          discountType: editValues.discountType,
          originalPrice: item.originalPrice || item.price
        };
      }
      return item;
    });

    setCart(updatedCart);
    setShowEditModal(false);
    setEditingItem(null);
  };

  return (
    <Card className="order-page-content-card" style={{'--animation-delay': '0s'}}>
      <Card.Header className="order-card-header">
        <h5 className="order-card-title">
          <FaBox className="order-card-icon" />
          Itens do Pedido
        </h5>
      </Card.Header>
      <Card.Body className="order-card-body">
        {cart.length === 0 ? (
          <div className="empty-cart">
            <FaBox className="empty-cart-icon" />
            <h5 className="empty-cart-title">Não há itens no pedido</h5>
            <p className="empty-cart-text">Adicione itens do catálogo para continuar</p>
          </div>
        ) : (
          <>
            {cart.map(item => {
              const finalPrice = getItemFinalPrice(item);
              const hasDiscount = item.discount && item.discount > 0;
              const originalPrice = item.customPrice || item.price;
              
              return (
                <div key={item.id} className="cart-item">
                  <div className="cart-item-left">
                    <div className="cart-item-details">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-1">{item.name}</h6>
                        {isAdmin && !readOnly && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={() => openEditModal(item)}
                          >
                            <FaEdit size={10} />
                          </Button>
                        )}
                      </div>
                      {item.description && (
                        <p className="cart-item-description">{item.description}</p>
                      )}
                      
                      <div className="cart-item-pricing">
                        {hasDiscount ? (
                          <>
                            <p className="cart-item-original-price text-decoration-line-through text-muted mb-1">
                              {formatCurrency(originalPrice)} por unidade
                            </p>
                            <p className="cart-item-price text-success mb-1">
                              {formatCurrency(finalPrice)} por unidade
                            </p>
                            <div className="discount-info">
                              <Badge bg="success" pill>
                                {item.discountType === 'percentage' 
                                  ? `${item.discount}% OFF`
                                  : `Desconto: ${formatCurrency(item.discount)}`
                                }
                              </Badge>
                            </div>
                          </>
                        ) : (
                          <p className="cart-item-price">
                            {formatCurrency(finalPrice)} por unidade
                            {item.customPrice && item.customPrice !== item.price && (
                              <Badge bg="info" pill className="ms-2">Preço Ajustado</Badge>
                            )}
                          </p>
                        )}
                      </div>
                      
                      {item.category && (
                        <Badge bg="secondary" pill className="mt-1">
                          {item.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="cart-item-right">
                    <div className="cart-quantity-control">
                      <button 
                        className="cart-quantity-btn"
                        onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                        disabled={readOnly}
                      >
                        <FaMinus size={12} />
                      </button>
                      <span className="cart-quantity-value">{item.quantity}</span>
                      <button 
                        className="cart-quantity-btn"
                        onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                        disabled={readOnly}
                      >
                        <FaPlus size={12} />
                      </button>
                    </div>
                    
                    <div className="cart-item-total">
                      <div className="cart-item-total-price">
                        {formatCurrency(finalPrice * item.quantity)}
                        {hasDiscount && (
                          <div className="cart-item-savings text-success">
                            <small>
                              Economia: {formatCurrency((originalPrice - finalPrice) * item.quantity)}
                            </small>
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <button 
                          className="cart-remove-btn"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <FaTrash size={10} /> Remover
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="cart-footer">
              {!readOnly && (
                <button 
                  className="cart-clear-btn"
                  onClick={clearCart}
                >
                  <FaTrash /> Limpar Carrinho
                </button>
              )}
              <div className="cart-total">
                <h5 className="cart-total-price">
                  Total: {formatCurrency(calculateCartTotal())}
                </h5>
                {calculateTotalDiscount() > 0 && (
                  <div className="cart-total-discount text-success">
                    <small>Desconto total: {formatCurrency(calculateTotalDiscount())}</small>
                  </div>
                )}
                <small className="cart-total-items">
                  {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'}
                </small>
              </div>
            </div>
          </>
        )}
      </Card.Body>

      {/* Modal de Edição de Item (Admin) */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)} 
        centered
        className="edit-item-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2" />
            Editar Item - {editingItem?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingItem && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Preço Original</Form.Label>
                <InputGroup>
                  <InputGroup.Text>R$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={(editingItem.originalPrice || editingItem.price).toFixed(2)}
                    disabled
                    className="bg-light"
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Este é o preço original do produto no catálogo.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Novo Preço *</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaDollarSign /></InputGroup.Text>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={editValues.price}
                    onChange={(e) => setEditValues({...editValues, price: e.target.value})}
                    placeholder="0.00"
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Defina um novo preço para este item no pedido.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Desconto</Form.Label>
                <Form.Select
                  value={editValues.discountType}
                  onChange={(e) => setEditValues({...editValues, discountType: e.target.value})}
                >
                  <option value="value">Valor fixo (R$)</option>
                  <option value="percentage">Porcentagem (%)</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Desconto</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    {editValues.discountType === 'percentage' ? <FaPercent /> : <FaDollarSign />}
                  </InputGroup.Text>
                  <Form.Control
                    type="number"
                    step={editValues.discountType === 'percentage' ? '1' : '0.01'}
                    min="0"
                    max={editValues.discountType === 'percentage' ? '100' : editValues.price}
                    value={editValues.discount}
                    onChange={(e) => setEditValues({...editValues, discount: e.target.value})}
                    placeholder="0"
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  {editValues.discountType === 'percentage' 
                    ? 'Desconto em porcentagem (0-100%)' 
                    : 'Desconto em valor fixo (R$)'}
                </Form.Text>
              </Form.Group>

              {/* Preview do preço final */}
              <Alert variant="info">
                <h6 className="mb-2">Preview do Item:</h6>
                <div className="d-flex justify-content-between">
                  <span>Preço base:</span>
                  <span>{formatCurrency(parseFloat(editValues.price) || 0)}</span>
                </div>
                {editValues.discount > 0 && (
                  <div className="d-flex justify-content-between text-success">
                    <span>Desconto:</span>
                    <span>
                      -{editValues.discountType === 'percentage' 
                        ? `${editValues.discount}%`
                        : formatCurrency(parseFloat(editValues.discount) || 0)
                      }
                    </span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="d-flex justify-content-between fw-bold">
                  <span>Preço final:</span>
                  <span>
                    {formatCurrency((() => {
                      const basePrice = parseFloat(editValues.price) || 0;
                      const discount = parseFloat(editValues.discount) || 0;
                      
                      if (editValues.discountType === 'percentage') {
                        return basePrice * (1 - discount / 100);
                      } else {
                        return Math.max(0, basePrice - discount);
                      }
                    })())}
                  </span>
                </div>
              </Alert>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={saveItemEdit}
            disabled={!editValues.price || parseFloat(editValues.price) <= 0}
          >
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ItensPedidoComponent;